import { motion } from "framer-motion";
import { Check, ChevronRight, Star, Scan } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

const PricingCard = ({ name, price, features, popular, badge, footnote }) => {
  return (
    <motion.div
      variants={fadeUp}
      className={`bg-white rounded-2xl p-8 relative ${popular ? "pricing-popular" : "border border-[#D9DADC]"}`}
      data-testid={`pricing-card-${name.toLowerCase()}`}
    >
      {popular && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-4 py-1 bg-[#E7A541] text-white text-sm font-semibold rounded-full">
          <Star size={14} className="fill-white" /> Recommandé
        </span>
      )}
      {badge && (
        <span className="inline-block px-3 py-1 bg-[#29282B] text-white text-xs font-semibold rounded-full mb-4">
          {badge}
        </span>
      )}
      <p className="text-sm uppercase tracking-wider text-[#29282B]/50 font-semibold mb-2">{name}</p>
      <div className="flex items-baseline gap-1 mb-2">
        <span className="font-display text-5xl font-bold text-[#29282B]">{price}€</span>
        <span className="text-[#29282B]/50">/an</span>
      </div>
      <p className="text-sm text-[#29282B]/50 mb-6">Licence annuelle par établissement</p>
      <div className="w-full h-px bg-[#D9DADC] mb-6" />
      <ul className="space-y-4 mb-8">
        {features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-3">
            <Check className="text-[#E7A541] flex-shrink-0 mt-0.5" size={18} />
            <span className="text-[#29282B]" dangerouslySetInnerHTML={{ __html: feature }} />
          </li>
        ))}
      </ul>
      {footnote && (
        <p className="text-xs text-[#29282B]/50 mb-4 italic">{footnote}</p>
      )}
      <a
        href="#contact"
        className={`block w-full py-3 rounded-full font-semibold transition-all duration-300 text-center ${
          popular
            ? "bg-[#E7A541] text-white hover:bg-[#D4913A]"
            : "border border-[#D9DADC] text-[#29282B] hover:border-[#E7A541]"
        }`}
        data-testid={`pricing-cta-${name.toLowerCase()}`}
      >
        Demander un devis
      </a>
    </motion.div>
  );
};

export const PricingSection = () => {
  return (
    <section id="pricing" className="py-24 bg-[#F9F9FA]" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#29282B] mb-4">
            Un tarif adapté à votre établissement
          </h2>
          <p className="text-[#29282B]/60 text-lg">Licence annuelle • Formation incluse • Support illimité</p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid md:grid-cols-3 gap-8 items-stretch">
          <PricingCard
            name="Starter"
            price="159"
            features={[
              "Jusqu'à <strong>300 élèves</strong>",
              "Jusqu'à <strong>20 professeurs</strong>",
              "Support par email",
              "Formation vidéos en ligne",
              "Mises à jour incluses"
            ]}
            popular={false}
          />
          <PricingCard
            name="Standard"
            price="299"
            features={[
              "Jusqu'à <strong>800 élèves</strong>",
              "Jusqu'à <strong>50 professeurs</strong>",
              "Support prioritaire",
              "<strong>Formation 1h</strong> avec les professeurs",
              "<strong>Formation 45min</strong> avec les délégués",
              "Formation de l'administration + 1h",
              "Mises à jour incluses"
            ]}
            popular={true}
          />
          <PricingCard
            name="Premium"
            price="399"
            badge="Tout inclus"
            features={[
              "<strong>Élèves illimités</strong>",
              "<strong>Professeurs illimités</strong>",
              "Support dédié",
              "Formation complète sur site",
              "<strong>Scan de l'établissement*</strong>",
              "Mises à jour incluses"
            ]}
            popular={false}
            footnote="* Scan : Analyse en présentiel et reproduction de l'ensemble des salles de votre établissement (gain de 12 à 17h)"
          />
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mt-12 bg-white rounded-xl p-6 border border-[#D9DADC]">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#FDF6E9] flex items-center justify-center">
                <Scan className="text-[#E7A541]" size={24} />
              </div>
              <div>
                <p className="font-semibold text-[#29282B]">Besoin d'un devis personnalisé ?</p>
                <p className="text-sm text-[#29282B]/60">Pour les académies, groupements d'établissements ou besoins spécifiques</p>
              </div>
            </div>
            <a 
              href="#contact" 
              className="inline-flex items-center px-6 py-3 bg-[#29282B] text-white rounded-full font-semibold hover:bg-[#29282B]/90 transition-all"
            >
              Contactez-nous
              <ChevronRight size={18} className="ml-1" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
